import React from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay/hooks';
import {FormattedMessage} from 'react-intl';
import moment from 'moment';
import useQuery from '../../../useQuery';
import i18n from '../../../i18n';
import splitPrice from '../splitPrice';
import ButtonIcon from '../../../components/button-icon/button-icon.component';
import CartAddCircle from '../../../assets/icons/cart-add-circle.svg';
import CartMinusCircle from '../../../assets/icons/cart-minus-circle.svg';
import getTagValue, {getServiceTagValue, isServiceSalesPaused} from '../../../utilities/get-tag-value';
import Currency from '../../../components/currency/currency';
import {FetchKeyStateContext} from '../../../components/FetchKey';
import useNow from '../../../useNow';
import classNames from 'classnames';
import {isBefore} from 'date-fns';
import {adopt} from '../../../math';
import {IN} from '../../../constants';

export const OrderItemRequestData = React.memo(({endpointId, serviceById: {start, end, strategy, every, id, description}, quantityInCart, addInvalidEndpoint, pick, children}) => {
    const fetchKeyState = React.useContext(FetchKeyStateContext);
    const {endpointById} = useLazyLoadQuery(
        graphql`
            query orderItemImpulseCheckoutPagesQuery($endpointId: String) {
                endpointById(id: $endpointId) {
                    id
                    name
                    nameUk
                    end
                    start
                    price
                    quantity
                    tags
                    labels
                    services {
                        id
                    }
                }
            }
        `,
        {endpointId: endpointId},
        {fetchKey: fetchKeyState, fetchPolicy: 'network-only'}
    );
    const now = useNow();
    const {state} = React.useMemo(() => adopt(now, start, end, strategy, every), [now, start, end, strategy, every]);
    const serviceActive = state === IN;
    const endpointBinded = endpointById.services.some((s) => s.id === id);
    const endpointActive = endpointById.end ? isBefore(now, new Date(endpointById.end)) : true;
    const salesPaused = isServiceSalesPaused(description);
    const endpointAvailable = !salesPaused && serviceActive && endpointBinded && endpointActive && endpointById.quantity > 0;
    React.useEffect(() => {
        if (!endpointAvailable) {
            addInvalidEndpoint({...pick, endpointData: endpointById});
        } else if (endpointById.quantity < pick.quantity) {
            addInvalidEndpoint({...pick, quantity: quantityInCart - endpointById.quantity, endpointData: endpointById})
        }
    }, [endpointAvailable, salesPaused]);
    return (
        <>
            {endpointAvailable && children({endpointById})}
        </>
    )
});

export const OrderItemDynamicQuantity = React.memo(({service, name, price, untie, quantity, nameUk, dispatch, stateKey, tags, labels}) => {
    const {locale = 'uk'} = useQuery();
    const splittedPrice = splitPrice(untie ? untie.price : price);
    const action = (type) => {
        dispatch({type, payload: stateKey});
    };
    const targetDate = getTagValue(tags, 'targetDate');
    const maxPerOrder = parseInt(labels?.maxPerOrder) || null;
    return (
        <>
            <div className='checkout-order-item padding-top-1rem padding-bottom-1rem'>
                <div className='flex-1 padding-right-1rem'>
                    <p className='text-md color-gray-700'>{i18n(service, 'name', locale)} | {i18n({name, nameUk}, 'name', locale)}</p>
                    {targetDate ?
                        <p className='text-md color-gray-500 margin-top-0dot25rem'>{targetDate}</p>
                        :
                        moment(service.target).isValid() && <p className='text-md color-gray-500 margin-top-0dot25rem'>{moment(service.target).format('DD.MM.YYYY')}</p>
                    }
                </div>
                <div className='display-flex justify-content-space-between padding-top-0dot5rem'>
                    <div className='display-flex align-items-center'>
                        <ButtonIcon {...{clickHandler: () => action('decrement'), disabled: quantity < 2}}>
                            <CartMinusCircle className='width-100percent'/>
                        </ButtonIcon>
                        <p className='color-primary-600 text-md padding-left-0dot25rem padding-right-0dot25rem min-width-2rem text-align-center'>{quantity}</p>
                        <ButtonIcon {...{clickHandler: () => action('increment'), disabled: !!maxPerOrder && quantity >= maxPerOrder}}>
                            <CartAddCircle className='width-100percent'/>
                        </ButtonIcon>
                    </div>
                    <p className='text-md color-gray-900 display-flex align-items-center align-self-flex-start'>
                        <span className='padding-right-0dot125rem'>
                            <span>{splittedPrice.whole}</span>
                            {splittedPrice.fraction &&
                                <>
                                    <span>.</span>
                                    <span className='text-xs medium'>{splittedPrice.fraction}</span>
                                </>
                            }
                            <span>{' '}</span>
                        </span>
                        <Currency {...{defaultCurrency: <FormattedMessage defaultMessage='UAH'/>, isStem: labels && labels.isStem, stemIconClasses: 'display-block width-1dot25rem height-1dot25rem'}}/>
                    </p>
                </div>
            </div>
            <div className='divider border-bottom-color-gray-200'/>
        </>
    )
});

const OrderItem = ({service, name, price, untie, quantity, nameUk, showOrderItemQuantity, tags, labels, invalid}) => {
    const {locale = 'uk'} = useQuery();
    const splittedPrice = splitPrice(untie ? untie.price : price);
    const targetDate = getTagValue(tags, 'targetDate');
    return (
        <>
            <div className='checkout-order-item display-flex padding-top-1rem padding-bottom-1rem'>
                {showOrderItemQuantity && <p className={classNames('text-md semibold padding-right-0dot625rem', {'color-blue-light-600': !invalid, 'color-gray-400': invalid})}>{quantity}x</p>}
                <div className='flex-1 padding-right-1rem'>
                    <p className={classNames('text-md', {'color-gray-700': !invalid, 'color-gray-400': invalid})}>{i18n(service, 'name', locale)} | {i18n({name, nameUk}, 'name', locale)}</p>
                    {targetDate ?
                        <p className={classNames('text-md margin-top-0dot25rem', {'color-gray-500': !invalid, 'color-gray-400': invalid})}>{targetDate}</p>
                        :
                        moment(service.target).isValid() && <p className={classNames('text-md margin-top-0dot25rem', {'color-gray-500': !invalid, 'color-gray-400': invalid})}>{moment(service.target).format('DD.MM.YYYY')}</p>}
                </div>
                <p className={classNames('text-md display-flex align-items-center align-self-flex-start', {'color-gray-900': !invalid, 'color-gray-400': invalid})}>
                    <span className='padding-right-0dot125rem'>
                        <span>{splittedPrice.whole}</span>
                        {splittedPrice.fraction &&
                            <>
                                <span>.</span>
                                <span className='text-xs medium'>{splittedPrice.fraction}</span>
                            </>
                        }
                        <span>{' '}</span>
                    </span>
                    <Currency {...{defaultCurrency: <FormattedMessage defaultMessage='UAH'/>, isStem: labels && labels.isStem, stemIconClasses: 'display-block width-1dot25rem height-1dot25rem'}}/>
                </p>
            </div>
            <div className='divider border-bottom-color-gray-200'/>
        </>
    )
};

export default React.memo(OrderItem);
